/**
 * Формирование чека по 54-ФЗ для виджета CloudPayments.
 *
 * ВАЖНО про то, куда класть чек. У виджета два API:
 *   - старый charge()/pay() с publicId — там чек кладут в options.data, и
 *     виджет сам перекладывает его в тело intent-запроса как
 *     metadata.JsonData (см. код чекаута: `metadata: { JsonData: n.data }`);
 *   - новый start() с publicTerminalId — им пользуемся мы. Здесь виджет
 *     отправляет опции в /api/intent как есть (`U = {...intentOptions}`),
 *     никакого маппинга data → metadata не происходит, и поле `data`
 *     просто отбрасывается API как неизвестное.
 *
 * Поэтому чек собираем сразу в виде metadata.JsonData — это ровно то тело,
 * которое получается у старого API и которое умеет читать касса.
 *
 * Ставка НДС, система налогообложения и признак предмета расчёта
 * настраиваются через .env домена: у разных франшиз разные реквизиты.
 */

/** Позиция чека (тег ФФД 1059) */
export interface CustomerReceiptItem {
  label: string;
  price: number;
  quantity: number;
  amount: number;
  vat: number | null;
  method: number;
  object: number;
}

export interface CustomerReceipt {
  Items: CustomerReceiptItem[];
  taxationSystem: number;
  calculationPlace: string;
  email: string;
  phone: string;
  isBso: boolean;
  amounts: {
    electronic: number;
    advancePayment: number;
    credit: number;
    provision: number;
  };
}

/** Значение опции `metadata` для widget.start() */
export interface ReceiptMetadata {
  JsonData: {
    CloudPayments: {
      CustomerReceipt: CustomerReceipt;
    };
  };
}

/** Число из .env; пустая строка или мусор — берём значение по умолчанию */
function envNumber<T extends number | null>(value: string | undefined, fallback: T): number | T {
  const raw = (value ?? '').trim();
  if (raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Система налогообложения (тег 1055):
 * 0 — ОСН, 1 — УСН доходы, 2 — УСН доходы минус расходы, 3 — ЕНВД, 4 — ЕСХН, 5 — ПСН
 */
const TAXATION_SYSTEM = envNumber(process.env.REACT_APP_RECEIPT_TAXATION_SYSTEM, 1);

/**
 * Ставка НДС (тег 1199). По умолчанию null («без НДС»).
 * Возможные значения: число 0, 5, 7, 10, 20 (а также 105, 107, 110, 120 для
 * расчётных ставок) либо `none` — «без НДС» (в чек уходит null).
 */
const VAT_DEFAULT = null;
const VAT_RAW = (process.env.REACT_APP_RECEIPT_VAT ?? '').trim().toLowerCase();
const VAT: number | null =
  VAT_RAW === 'none' || VAT_RAW === 'null' ? null : envNumber(VAT_RAW, VAT_DEFAULT);

/**
 * Признак способа расчёта (тег 1214): 0 — полный расчёт.
 */
const METHOD = envNumber(process.env.REACT_APP_RECEIPT_METHOD, 0);

/**
 * Признак предмета расчёта (тег 1212): 0 — товар, 3 — услуга.
 * Подписка на сервис — услуга.
 */
const OBJECT = envNumber(process.env.REACT_APP_RECEIPT_OBJECT, 0);

/** Место расчётов (тег 1187) — адрес сайта, на котором прошла оплата */
function getCalculationPlace(): string {
  return process.env.REACT_APP_SITE_URL || window.location.hostname;
}

/** Округление до копеек, чтобы сумма позиции сходилась с суммой платежа */
function toMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Собирает значение опции `metadata` для widget.start(): чек на одну
 * позицию — оплату выбранного тарифа.
 */
export function buildReceiptMetadata(params: {
  /** Наименование позиции в чеке, например «Тариф "Базовый"» */
  label: string;
  /** Сумма платежа в рублях */
  amount: number;
  /** Email покупателя — на него касса отправит чек */
  email: string;
  /** Телефон покупателя, если есть */
  phone?: string;
}): ReceiptMetadata {
  const amount = toMoney(params.amount);

  const receipt: CustomerReceipt = {
    Items: [
      {
        label: params.label,
        price: amount,
        quantity: 1,
        amount,
        vat: VAT,
        method: METHOD,
        object: OBJECT,
      },
    ],
    taxationSystem: TAXATION_SYSTEM,
    calculationPlace: getCalculationPlace(),
    email: params.email,
    phone: params.phone || '',
    isBso: false,
    amounts: {
      electronic: amount,
      advancePayment: 0,
      credit: 0,
      provision: 0,
    },
  };

  return { JsonData: { CloudPayments: { CustomerReceipt: receipt } } };
}
